import {
    get, post, put, patch, del, isCancelError,
} from 'aws-amplify/api';

import { RequestConfig } from './interfaces';

/**
 * v6 REST operation returned by `get/post/...`. Holds a `response` promise and a
 * `cancel()` method (v6 replaced the v5 `API.cancel(promise)` model).
 */
export interface AmplifyRestOperation {
    response: Promise<AmplifyRestResponse>;
    cancel: (abortMessage?: string) => void;
}

interface AmplifyRestResponse {
    body: { text: () => Promise<string>; json: () => Promise<unknown>; blob: () => Promise<Blob> };
    statusCode: number;
    headers: Record<string, string>;
}

/**
 * v5-compatible response object (axios-like) returned when the caller opts in
 * via `init.response === true`. The whole app reads `.data` / `.status` /
 * `.headers` from these, so the shape must match axios, not the raw v6 shape
 * (`{ body, statusCode }`).
 */
interface AxiosLikeResponse<T = unknown> {
    data: T;
    status: number;
    statusText: string;
    headers: Record<string, string>;
    config: Record<string, unknown>;
    request: Record<string, unknown>;
}

type V6Method = 'get' | 'post' | 'put' | 'patch' | 'del';

type V6Input = { apiName: string; path: string; options?: Record<string, unknown> };

const V6_METHODS: Record<V6Method, (input: V6Input) => AmplifyRestOperation> = {
    get: get as never,
    post: post as never,
    put: put as never,
    patch: patch as never,
    del: del as never,
};

const parseMaybeJson = (text: string): unknown => {
    if (!text) {
        // Mirror v5: empty body -> empty string (safe to destructure).
        return '';
    }
    try {
        return JSON.parse(text);
    } catch {
        return text;
    }
};

const looksLikeNetworkError = (error: { name?: string; message?: string }): boolean => {
    const message = (error?.message || '').toLowerCase();
    return (
        error?.name === 'TypeError'
        || message.includes('failed to fetch')
        || message.includes('network')
        || message.includes('load failed')
    );
};

/**
 * Translate a v6 REST error into the axios-shaped error the app expects
 * (`error.response.status` / `error.response.data.message`), or preserve a
 * cancellation so `isCancelError` keeps working. Never returns.
 */
const throwAxiosLikeError = (error: unknown): never => {
    if (isCancelError(error)) {
        throw error;
    }
    const restError = error as {
        name?: string;
        message?: string;
        response?: { statusCode: number; headers?: Record<string, string>; body?: string };
    };
    const response = restError?.response;
    if (response && typeof response.statusCode === 'number') {
        const axiosError = new Error(restError.message || 'Request failed') as Error & {
            name: string;
            isAxiosError: boolean;
            underlyingError: unknown;
            response: AxiosLikeResponse;
        };
        axiosError.name = restError.name || 'Error';
        axiosError.isAxiosError = true;
        axiosError.underlyingError = error;
        axiosError.response = {
            data: parseMaybeJson(response.body ?? ''),
            status: response.statusCode,
            statusText: '',
            headers: response.headers ?? {},
            config: {},
            request: {},
        };
        throw axiosError;
    }
    if (looksLikeNetworkError(restError)) {
        // Preserve v5/axios semantics used by `checkIsNetworkError`.
        const networkError = new Error('Network Error') as Error & { underlyingError: unknown };
        networkError.underlyingError = error;
        throw networkError;
    }
    throw error;
};

const buildOptions = (init: RequestConfig = {}): Record<string, unknown> => {
    const {
        headers, body, queryParams, response, signal, ...rest
    } = init;
    const options: Record<string, unknown> = { ...rest };
    if (headers) {
        options.headers = headers;
    }
    if (body !== undefined) {
        options.body = body;
    }
    if (queryParams) {
        options.queryParams = queryParams;
    }
    return options;
};

const resolveResponse = async (
    operation: AmplifyRestOperation,
    init: RequestConfig = {},
): Promise<unknown> => {
    let restResponse: AmplifyRestResponse;
    try {
        restResponse = await operation.response;
    } catch (error) {
        return throwAxiosLikeError(error);
    }
    const text = await restResponse.body.text();
    const data = parseMaybeJson(text);
    if (init.response) {
        const axiosLike: AxiosLikeResponse = {
            data,
            status: restResponse.statusCode,
            statusText: '',
            headers: restResponse.headers,
            config: {},
            request: {},
        };
        return axiosLike;
    }
    return data;
};

/**
 * v6 REST client wrapped in the same `SPXAPI` (`BaseAdapter`) surface the app
 * used for the v5 `APIClass`, so `EndpointsResolver`, `CancellableAPI` and all
 * direct call sites keep working unchanged. Preserves IAM/SigV4 signing (handled
 * automatically by the v6 REST client) and returns/throws axios-shaped values.
 */
/* eslint-disable class-methods-use-this */
class AmplifyV6RestAdapter {
    /** Build the raw v6 operation (used by CancellableAPI so it can `cancel()`). */
    operation(
        method: V6Method,
        apiName: string,
        endpoint: string,
        init: RequestConfig = {},
    ): AmplifyRestOperation {
        return V6_METHODS[method]({ apiName, path: endpoint, options: buildOptions(init) });
    }

    /** Await an operation and normalise the response/errors to the v5 shape. */
    resolve(operation: AmplifyRestOperation, init: RequestConfig = {}): Promise<unknown> {
        return resolveResponse(operation, init);
    }

    private async call(
        method: V6Method,
        apiName: string,
        endpoint: string,
        init: RequestConfig = {},
    ): Promise<unknown> {
        const operation = this.operation(method, apiName, endpoint, init);
        return resolveResponse(operation, init);
    }

    async get(apiName: string, endpoint: string, init: RequestConfig = {}): Promise<unknown> {
        return this.call('get', apiName, endpoint, init);
    }

    async post(apiName: string, endpoint: string, init: RequestConfig = {}): Promise<unknown> {
        return this.call('post', apiName, endpoint, init);
    }

    async put(apiName: string, endpoint: string, init: RequestConfig = {}): Promise<unknown> {
        return this.call('put', apiName, endpoint, init);
    }

    async patch(apiName: string, endpoint: string, init: RequestConfig = {}): Promise<unknown> {
        return this.call('patch', apiName, endpoint, init);
    }

    async del(apiName: string, endpoint: string, init: RequestConfig = {}): Promise<unknown> {
        return this.call('del', apiName, endpoint, init);
    }
}

export default AmplifyV6RestAdapter;

/** Ready-to-use singleton for direct call sites that used the raw Amplify `API`. */
export const amplifyRestApi = new AmplifyV6RestAdapter();
